import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useStockLocations } from '../../data/catalog.ts'
import { buildCountOpenPayload, countsOfSite, COUNT_PROBLEM_KEY, openCountAt, useStockCountLines, useStockCounts, type StockCountRow } from '../../data/countUi.ts'
import { locationLabel, siteLocations } from '../../data/stockUi.ts'
import { formatLocal } from '../../lib/time.ts'
import { BackLink, DisabledHint, Field, NotAllowed, Pill } from '../../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { Picto, PictoBadge } from '../../ui/pictos.tsx'
import { Feedback, useLang, useStockAccess } from './shared.tsx'

/** How many closed counts the list shows (the newest; older ones stay in the ledger and the export). */
export const CLOSED_SHOWN = 12

/** M15-B, Catalogue › Stock › Inventaires (docs/M15.md "Screens"): the counts of the site's places, the open ones first, each a 56-px row to its page; for the
 *  storeroom (stock.manage) "Ouvrir un inventaire" on a place (an online intent: the counting itself works with no signal). Read from the device's copy:
 *  works offline. Whoever records stock movements (stock.record) sees the list to count; the page is `/catalogue/count` (Z's route). */
export function CountListPage() {
  const { t } = useTranslation()
  const lang = useLang()
  const { site } = useClub()
  const record = useWriteAccess('stock.record')
  const manage = useStockAccess()
  const counts = useStockCounts(); const lines = useStockCountLines(); const locations = useStockLocations()
  const place = useMemo(() => new Map((locations ?? []).map((l) => [l.id, locationLabel(l, lang)])), [locations, lang])
  const rows = useMemo(() => countsOfSite(counts ?? [], locations ?? [], site.site_id), [counts, locations, site.site_id])
  const lineCount = useMemo(() => { const m = new Map<string, number>(); for (const l of lines ?? []) m.set(l.count_id, (m.get(l.count_id) ?? 0) + 1); return m }, [lines])
  if (!record.permitted && !manage.permitted) return <section className="flex flex-col gap-4" data-testid="count-list-page"><h1 className="text-2xl font-bold">{t('count.title')}</h1><NotAllowed permission="stock.record" /></section>
  if (!counts || !lines || !locations) return <p>{t('common.loading')}</p>
  const open = rows.filter((c) => c.status === 'open')
  const closed = rows.filter((c) => c.status !== 'open').slice(0, CLOSED_SHOWN)
  const row = (c: StockCountRow) => (
    <li key={c.id}>
      {/* the RowLink pattern (56 px, a badge, the words, a chevron) with the count's id as the route's parameter */}
      <Link to="/catalogue/count/$countId" params={{ countId: c.id }} className="row-link" data-testid="count-row" data-count={c.id} data-status={c.status}>
        <PictoBadge name="clipboard" tone={c.status === 'open' ? 'sand' : 'green'} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-lg font-semibold leading-snug">{place.get(c.location_id) ?? '…'}</span>
          <span className="meta">{[c.status === 'open' ? t('count.list.openedOn', { date: formatLocal(c.opened_at, site.timezone, 'dd/MM/yyyy') }) : t('count.list.closedOn', { date: formatLocal(c.closed_at ?? c.opened_at, site.timezone, 'dd/MM/yyyy') }),
            t('count.list.lines', { count: lineCount.get(c.id) ?? 0 })].join(' · ')}</span>
        </span>
        {c.status === 'open' && <Pill tone="amber">{t('count.page.statusOpen')}</Pill>}
        <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
      </Link>
    </li>
  )
  return (
    <section className="flex flex-col gap-4" data-testid="count-list-page">
      <BackLink to="/catalogue" search={{ tab: 'stock' }}>← {t('stock.title')}</BackLink>
      <h1 className="text-2xl font-bold">{t('count.title')}</h1>
      <p className="meta">{t('count.help')}</p>
      {manage.permitted && <OpenCount counts={counts} />}
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-bold">{t('count.list.open')}</h2>
        {open.length === 0 ? <p className="meta" data-testid="count-open-none">{t('count.list.noneOpen')}</p> : <ul className="flex flex-col gap-2" data-testid="count-open-list">{open.map(row)}</ul>}
      </div>
      {closed.length > 0 && (
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-bold">{t('count.list.closed')}</h2>
          <ul className="flex flex-col gap-2" data-testid="count-closed-list">{closed.map(row)}</ul>
        </div>
      )}
    </section>
  )
}

/** "Ouvrir un inventaire": a place of the site (the default first), a note; refused on the device when the place already has one open (the server says it too). */
function OpenCount({ counts }: { counts: readonly StockCountRow[] }) {
  const { t } = useTranslation()
  const lang = useLang()
  const { site } = useClub()
  const access = useStockAccess()
  const w = useWorkshopAction()
  const navigate = useNavigate()
  const locations = useStockLocations()
  const id = useRef(newId())
  const [locationId, setLocationId] = useState('')
  const [note, setNote] = useState('')
  const places = useMemo(() => siteLocations(locations ?? [], site.site_id), [locations, site.site_id])
  const chosen = places.some((l) => l.id === locationId) ? locationId : places[0]?.id ?? ''
  const already = chosen ? openCountAt(counts, chosen) : undefined
  const problem = !chosen ? COUNT_PROBLEM_KEY.place : !access.online ? 'count.open.offline' : null
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!chosen || already || !access.canSave) return
    void (async () => {
      const countId = id.current
      const ok = await w.run('stock.count_open@1', buildCountOpenPayload({ countId, locationId: chosen, note }), 'count.open.saved', { aggregateId: countId })
      if (!ok) return
      id.current = newId(); setNote('')
      void navigate({ to: '/catalogue/count/$countId', params: { countId } })
    })()
  }
  return (
    <form onSubmit={submit} noValidate className="card flex flex-col gap-3" data-testid="count-open-form">
      <h2 className="text-xl font-bold">{t('count.open.title')}</h2>
      <Field label={t('count.open.place')} htmlFor="co-place">
        <select id="co-place" className="input" value={chosen} onChange={(e) => setLocationId(e.target.value)} data-testid="count-open-place">
          {places.map((l) => <option key={l.id} value={l.id}>{locationLabel(l, lang)}</option>)}
        </select>
      </Field>
      <Field label={t('count.open.note')} htmlFor="co-note"><input id="co-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="count-open-note" /></Field>
      {already && (
        <p className="flex flex-wrap items-center gap-2 font-semibold" data-testid="count-open-already">
          {t('count.open.already')}
          <Link to="/catalogue/count/$countId" params={{ countId: already.id }} className="btn" data-testid="count-open-go">{t('count.open.goOpen')}</Link>
        </p>
      )}
      <Feedback id="count-open" error={w.error} pending={w.pending} />
      <DisabledHint show={!already && !!problem} testId="count-open-hint">{problem ? t(problem) : ''}</DisabledHint>
      <button className="btn btn-primary" disabled={w.busy || !!already || !!problem || !access.canSave} data-testid="count-open-go-btn">{w.busy ? t('common.saving') : t('count.open.go')}</button>
    </form>
  )
}
